import React, { useState } from 'react';
import { Content } from 'src/components/light/Content';
import { SectionBlock } from 'src/components/light/SectionBlock';
import { gql, useQuery } from '@apollo/client';
import { Loading } from 'src/components/Loading';
import { Link } from 'react-router-dom';
import moment from 'moment';
import downloadExcelFile from 'src/libs/downloadExcelFile';
import { DailyInvoicePdfExport } from 'src/components/pdf/DailyInvoicePdfExport';

const QUERY_INVOICE_LIST_V2 = gql`
  query reportInvoiceListV2($year: Int!, $month: Int!) {
    reportInvoiceListV2(year: $year, month: $month)
  }
`;

export function InventorySaleInvoiceListScreen() {
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [hasSearched, setHasSearched] = useState(false);

  const [inputYear, setInputYear] = useState(currentDate.getFullYear().toString());
  const [inputMonth, setInputMonth] = useState((currentDate.getMonth() + 1).toString().padStart(2, '0'));

  const [searchInvoice, setSearchInvoice] = useState('');
  const [quickPreviewInvoice, setQuickPreviewInvoice] = useState('');

  const { loading, data } = useQuery(QUERY_INVOICE_LIST_V2, {
    variables: { year, month },
    fetchPolicy: 'network-only',
    skip: !hasSearched,
  });

  const onReportSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setYear(Number(inputYear));
    setMonth(Number(inputMonth));
    setHasSearched(true);
  };

  const onQuickPreview = (e: React.FormEvent) => {
    e.preventDefault();
    if (quickPreviewInvoice.trim()) {
      window.open(`/invoices/inventory-sale/${quickPreviewInvoice.trim()}`, '_blank');
    }
  };

  const onExportExcel = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rows: any[][] = [
      ['Invoice No', 'Date', 'Customer', 'Description', 'Qty', 'Amount (USD)', 'COGS (USD)'],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...invoices.map((inv: any) => [
        inv.invoice_no,
        moment.utc(inv.invoice_date).format('YYYY-MM-DD'),
        inv.customer_name || '',
        inv.description || '',
        inv.qty || 0,
        Number(inv.amount || 0),
        Number(inv.cogs || 0),
      ]),
    ];
    downloadExcelFile(`Invoices_${year}_${month}.xlsx`, 'Invoices', rows);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let invoices: any[] = [];
  if (data && data.reportInvoiceListV2) {
    invoices = data.reportInvoiceListV2;
  }

  if (searchInvoice.trim()) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    invoices = invoices.filter((inv: any) => inv.invoice_no.toLowerCase().includes(searchInvoice.toLowerCase()));
  }

  return (
    <Content>
      <SectionBlock title="Inventory Sale Invoices">
        <div className="row mb-3">
          <div className="col-sm-6">
            <form className="form-inline" onSubmit={onReportSubmit}>
              <input
                type="text"
                placeholder="Year"
                className="form-control form-control-sm rounded bright"
                style={{ marginRight: 7, width: 75 }}
                value={inputYear}
                onChange={e => setInputYear(e.target.value)}
              />
              <input
                type="text"
                placeholder="Month"
                className="form-control form-control-sm rounded bright"
                style={{ marginRight: 7, width: 75 }}
                value={inputMonth}
                onChange={e => setInputMonth(e.target.value)}
              />
              <button className="btn btn-secondary btn-sm" type="submit">
                Filter
              </button>
            </form>
          </div>
          <div className="col-sm-6 text-right">
            <form className="form-inline justify-content-end" onSubmit={onQuickPreview}>
              <input
                type="text"
                placeholder="Quick preview by Invoice No."
                className="form-control form-control-sm rounded bright"
                style={{ marginRight: 7, width: 250 }}
                value={quickPreviewInvoice}
                onChange={e => setQuickPreviewInvoice(e.target.value)}
              />
              <button className="btn btn-primary btn-sm" type="submit">
                Preview / Print
              </button>
            </form>
          </div>
        </div>

        <div className="mb-3 d-flex justify-content-between align-items-center">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search invoice number in this month..."
            value={searchInvoice}
            onChange={e => setSearchInvoice(e.target.value)}
            style={{ width: '300px' }}
          />
          <div className="d-flex align-items-center">
            <DailyInvoicePdfExport invoices={invoices} type="inventory-sale" filePrefix="InventorySale" />
            <button className="btn btn-success btn-sm" onClick={onExportExcel} disabled={invoices.length === 0}>
              Export Excel
            </button>
          </div>
        </div>

        {loading ? (
          <Loading />
        ) : (
          <div className="table-responsive">
            <table className="table table-lightborder table-hover table-sticky-header">
              <thead className="thead-light">
                <tr>
                  <th>Invoice No</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Amount (USD)</th>
                  <th>COGS</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {invoices.map((inv: any, idx: number) => (
                  <tr key={idx}>
                    <td>
                      <Link target="_blank" to={`/invoices/inventory-sale/${inv.invoice_no}`}>
                        {inv.invoice_no}
                      </Link>
                    </td>
                    <td>{moment.utc(inv.invoice_date).format('YYYY-MM-DD')}</td>
                    <td>{inv.customer_name}</td>
                    <td>{inv.description}</td>
                    <td>{inv.qty}</td>
                    <td>${Number(inv.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td>${Number(inv.cogs || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td>
                      <Link
                        target="_blank"
                        className="btn btn-sm btn-primary"
                        to={`/invoices/inventory-sale/${inv.invoice_no}`}
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
                {hasSearched && invoices.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center">
                      No invoices found.
                    </td>
                  </tr>
                )}
                {!hasSearched && (
                  <tr>
                    <td colSpan={8} className="text-center">
                      Please enter a Year and Month to view invoices.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </SectionBlock>
    </Content>
  );
}
